/**
 * @file advanced-keys.ts
 * @description GCOME_AP 高级按键（DKS / MT / TGL / RS / SOCS / OKS）信息区读写与解析。
 *
 * 协议细节（参见 docs/果来微科技_磁轴键盘AP通讯协议.md）：
 * - DKS 动态行程信息区：CMD 0xa2 GetDksInfo / 0xa3 SetDksInfo，每板载 768 字节，
 *   最多 32 个 slot，每个 slot 24 字节：
 *     - 前 4 字节：四个触发键程 TravelA/B/C/D（每字节 0-39）
 *     - 后 20 字节：四组按键信息，每组 5 字节 =
 *       3 字节按键功能（Type/CodeL/CodeH）+ 1 字节触点（0-39）+ 2 字节触点状态
 * - MT 按键信息区：CMD 0xa4 GetMtKeyInfo / 0xa5 SetMtKeyInfo，每板载 256 字节，
 *   最多 32 个 slot，每个 slot 6 字节 = 按键1单击功能（Type/CodeL/CodeH）
 *   + 按键2长按/绑定功能（Type/CodeL/CodeH）。
 *   MT slot 即"两个键位互绑"表项（tap=单击功能 / hold=长按功能），无类型标记；
 *   矩阵键 0x93/0x94/0x95（迅捷/后覆盖/急停绑定键）的查表项也存于本区——
 *   当前 SDK 不提供这几种绑定键的专门模型，仅作原始透传（见 keyboard-keys.ts）。
 * - TGL 按键信息区：CMD 0xa6 GetTglKeyInfo / 0xa7 SetTglKeyInfo，每板载 128 字节，
 *   最多 32 个 slot，每个 slot 3 字节 = 按键功能（Type/CodeL/CodeH）。
 * - 触发行程参数区：CMD 0xa0 GetKeyTriggerTravel / 0xa1 SetKeyTriggerTravel，每板载
 *   1024 字节 = 128 键 × 8 字节/键：
 *     - 字节0：低5bit 轴体类型，高3bit 校验（固定 0x5）
 *     - 字节1：低4bit 触发类型（0 普通 / 1 RT 关连续 / 2 RT 开连续），
 *       高4bit SOCD 优先级策略（0x00 后覆盖 / 0x10 自身优先 / 0x20 绑定优先 / 0x30 同时不生效）
 *     - 字节2-3：触发行程（0-399，精度 0.01mm）；字节3 bit1-4：精度切换
 *     - 字节4-5：按下参数（低 9 位 = 按下RT 0-399，高 7 位 = 按下死区 0-127，0.01mm）
 *     - 字节6-7：释放参数（低 9 位 = 释放RT 0-399，高 7 位 = 释放死区 0-127，0.01mm）
 *   RS/SOCS/OKS 的行程与快速触发即配置于被绑定键（矩阵键 0x93/0x94/0x95 的 Code1）的参数区。
 *
 * 信息区地址模式与按键矩阵一致：第 N 个板载起始地址 = N × capacity（见 address.ts）。
 */

import type { BaseDevice } from '../../../runtime/BaseDevice'
import type {
  DksContactState,
  DksContactStateSeg,
  DksSlotInfo,
  MtSlotInfo,
  TglSlotInfo,
} from '../../../types/keyboardKeys'
import type {
  GcomeKeyTravelInfo,
  GcomeSocdStrategy,
  GcomeTriggerType,
} from '../../../types/keyTravel'
import { KeyTravelFamily } from '../../../types/keyTravel'
import { SHAFT_TYPE_HEAD, SHAFT_TYPE_ID_MASK } from '../../../types/shaftType'
import { GCOME_AP_CMD, GCOME_AP_REPORT } from '../protocols/cmd'
import { calculateGcomeApAddress } from '../protocols/address'

export const DKS_SLOT_COUNT = 32
export const MT_SLOT_COUNT = 32
export const TGL_SLOT_COUNT = 32
export const TRAVEL_KEY_COUNT = 128

export const DKS_SLOT_SIZE = 24
export const MT_SLOT_SIZE = 6
export const TGL_SLOT_SIZE = 3
export const TRAVEL_KEY_SIZE = 8

export const DKS_INFO_SIZE = DKS_SLOT_COUNT * DKS_SLOT_SIZE // 768
export const MT_INFO_SIZE = MT_SLOT_COUNT * MT_SLOT_SIZE // 192
export const TGL_INFO_SIZE = TGL_SLOT_COUNT * TGL_SLOT_SIZE // 96
export const TRAVEL_INFO_SIZE = TRAVEL_KEY_COUNT * TRAVEL_KEY_SIZE // 1024

/** GCOME_AP 设备发送 AP 命令的最小接口。 */
interface GcomeApSendDevice {
  sendRawApCommand(request: {
    command: number
    address?: number
    data?: number[] | Uint8Array
  }): Promise<{ response: { data: Uint8Array } }>
}

// ==================== 基础读写 ====================

/** 读取当前选中的板载配置分组（GetBase 偏移 0）。 */
export async function getActiveProfile(device: BaseDevice): Promise<number> {
  const gcome = device as unknown as GcomeApSendDevice
  const res = await gcome.sendRawApCommand({
    command: GCOME_AP_CMD.getBase,
    data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
  })
  return res.response.data[0] ?? 0
}

/** 信息区分帧读写进度回调：done/total 为已完成帧数/总帧数。 */
export type InfoBlockProgress = (done: number, total: number) => void

/**
 * 分帧读取一个信息区的完整原始字节。
 * @param device 设备
 * @param command 读取命令（GetDksInfo / GetMtKeyInfo / GetTglKeyInfo）
 * @param size 信息区总大小
 * @param profile 板载序号，缺省取当前活动板载
 * @param onProgress 分帧进度回调（可选）
 */
export async function readInfoBlock(
  device: BaseDevice,
  command: number,
  size: number,
  profile?: number,
  onProgress?: InfoBlockProgress,
): Promise<Uint8Array> {
  const gcome = device as unknown as GcomeApSendDevice
  const activeProfile = profile ?? (await getActiveProfile(device))
  const base = calculateGcomeApAddress(command, activeProfile)
  const chunks: Uint8Array[] = []
  const maxFrames = Math.ceil(size / GCOME_AP_REPORT.payloadSize)

  for (let i = 0; i < maxFrames; i++) {
    const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, size - i * GCOME_AP_REPORT.payloadSize)
    const address = base + i * GCOME_AP_REPORT.payloadSize
    const res = await gcome.sendRawApCommand({
      command,
      address,
      data: new Array(frameLen).fill(0),
    })
    const chunk = res.response.data
    chunks.push(chunk)
    onProgress?.(i + 1, maxFrames)
    if (chunk.length < frameLen) break
  }

  const result = new Uint8Array(size)
  let offset = 0
  for (const chunk of chunks) {
    if (offset >= size) break
    const len = Math.min(chunk.length, size - offset)
    result.set(chunk.slice(0, len), offset)
    offset += len
  }
  return result
}

/** 读取 DKS 动态行程信息区（768 字节）。 */
export function readDksInfoBlock(device: BaseDevice, profile?: number): Promise<Uint8Array> {
  return readInfoBlock(device, GCOME_AP_CMD.getDksInfo, DKS_INFO_SIZE, profile)
}

/** 读取 MT 按键信息区（192 字节）。 */
export function readMtInfoBlock(device: BaseDevice, profile?: number): Promise<Uint8Array> {
  return readInfoBlock(device, GCOME_AP_CMD.getMtKeyInfo, MT_INFO_SIZE, profile)
}

/** 读取 TGL 按键信息区（96 字节）。 */
export function readTglInfoBlock(device: BaseDevice, profile?: number): Promise<Uint8Array> {
  return readInfoBlock(device, GCOME_AP_CMD.getTglKeyInfo, TGL_INFO_SIZE, profile)
}

/** 读取触发行程参数区（1024 字节，128 键 × 8 字节）。 */
export function readTravelBlock(
  device: BaseDevice,
  profile?: number,
  onProgress?: InfoBlockProgress,
): Promise<Uint8Array> {
  return readInfoBlock(
    device,
    GCOME_AP_CMD.getKeyTriggerTravel,
    TRAVEL_INFO_SIZE,
    profile,
    onProgress,
  )
}

/**
 * 写入一个信息区的完整原始字节（分帧下发）。
 * @param device 设备
 * @param command 写入命令（SetDksInfo / SetMtKeyInfo / SetTglKeyInfo）
 * @param data 完整信息区字节（长度需 ≤ 信息区大小）
 * @param profile 板载序号，缺省取当前活动板载
 * @param onProgress 分帧进度回调（可选）
 */
export async function writeInfoBlock(
  device: BaseDevice,
  command: number,
  data: Uint8Array,
  profile?: number,
  onProgress?: InfoBlockProgress,
): Promise<void> {
  const gcome = device as unknown as GcomeApSendDevice
  const activeProfile = profile ?? (await getActiveProfile(device))
  const base = calculateGcomeApAddress(command, activeProfile)
  const maxFrames = Math.ceil(data.length / GCOME_AP_REPORT.payloadSize)

  for (let i = 0; i < maxFrames; i++) {
    const start = i * GCOME_AP_REPORT.payloadSize
    const end = Math.min(start + GCOME_AP_REPORT.payloadSize, data.length)
    await gcome.sendRawApCommand({
      command,
      address: base + start,
      data: Array.from(data.slice(start, end)),
    })
    onProgress?.(i + 1, maxFrames)
  }
}

/** 写入 DKS 动态行程信息区。 */
export function writeDksInfoBlock(
  device: BaseDevice,
  data: Uint8Array,
  profile?: number,
): Promise<void> {
  return writeInfoBlock(device, GCOME_AP_CMD.setDksInfo, data, profile)
}

/** 写入 MT 按键信息区。 */
export function writeMtInfoBlock(
  device: BaseDevice,
  data: Uint8Array,
  profile?: number,
): Promise<void> {
  return writeInfoBlock(device, GCOME_AP_CMD.setMtKeyInfo, data, profile)
}

/** 写入 TGL 按键信息区。 */
export function writeTglInfoBlock(
  device: BaseDevice,
  data: Uint8Array,
  profile?: number,
): Promise<void> {
  return writeInfoBlock(device, GCOME_AP_CMD.setTglKeyInfo, data, profile)
}

/** 写入触发行程参数区。 */
export function writeTravelBlock(
  device: BaseDevice,
  data: Uint8Array,
  profile?: number,
  onProgress?: InfoBlockProgress,
): Promise<void> {
  return writeInfoBlock(device, GCOME_AP_CMD.setKeyTriggerTravel, data, profile, onProgress)
}

// ==================== 空 slot 构造（删除/清空语义） ====================

const EMPTY_CONTACT_STATE_SEG = { selected: false, continuous: false, nextPointState: false }

/** 构造一个空的 DKS 触点状态。 */
export function createEmptyDksContactState(): DksContactState {
  return {
    segments: [
      { ...EMPTY_CONTACT_STATE_SEG },
      { ...EMPTY_CONTACT_STATE_SEG },
      { ...EMPTY_CONTACT_STATE_SEG },
    ],
    final: false,
  }
}

/** 构造一个空的 DKS slot（全部字节为 0，等价于未配置）。 */
export function createEmptyDksSlot(index: number): DksSlotInfo {
  const state = createEmptyDksContactState()
  return {
    index,
    travels: [0, 0, 0, 0],
    keys: [
      { type: 0, codeL: 0, codeH: 0, state },
      { type: 0, codeL: 0, codeH: 0, state },
      { type: 0, codeL: 0, codeH: 0, state },
      { type: 0, codeL: 0, codeH: 0, state },
    ] as DksSlotInfo['keys'],
  }
}

/** 构造一个空的 MT slot（单击 + 长按/绑定均为 0）。 */
export function createEmptyMtSlot(index: number): MtSlotInfo {
  return {
    index,
    tap: { type: 0, codeL: 0, codeH: 0 },
    hold: { type: 0, codeL: 0, codeH: 0 },
  }
}

/** 构造一个空的 TGL slot（按键功能全 0）。 */
export function createEmptyTglSlot(index: number): TglSlotInfo {
  return { index, type: 0, codeL: 0, codeH: 0 }
}

// ==================== 触点状态位域 ====================

/** 解析 DKS 触点状态原始值（低 10 位有效）为位域结构。 */
export function parseDksContactState(state: number): DksContactState {
  const seg = (start: number): DksContactStateSeg => ({
    selected: ((state >> start) & 1) === 1,
    continuous: ((state >> (start + 1)) & 1) === 1,
    nextPointState: ((state >> (start + 2)) & 1) === 1,
  })
  return {
    segments: [seg(0), seg(3), seg(6)],
    final: ((state >> 9) & 1) === 1,
  }
}

/** 将触点状态位域结构编码回原始值（低 10 位有效）。 */
export function encodeDksContactState(s: DksContactState): number {
  let v = s.final ? 1 << 9 : 0
  s.segments.forEach((seg, i) => {
    const start = i * 3
    if (seg.selected) v |= 1 << start
    if (seg.continuous) v |= 1 << (start + 1)
    if (seg.nextPointState) v |= 1 << (start + 2)
  })
  return v & 0x3ff
}

// ==================== 解析 ====================

/**
 * 解析 DKS 信息区为 slot 列表。
 * 触点状态已由 raw 转换为结构化 DksContactState（UI 直接消费）。
 * 空 slot（全部字节为 0 或 0xff）保持原样解析，由调用方决定是否过滤展示。
 */
export function parseDksInfo(block: Uint8Array, count = DKS_SLOT_COUNT): DksSlotInfo[] {
  const items: DksSlotInfo[] = []
  for (let i = 0; i < count; i++) {
    const base = i * DKS_SLOT_SIZE
    const travels: [number, number, number, number] = [
      block[base],
      block[base + 1],
      block[base + 2],
      block[base + 3],
    ]
    const keys = [0, 1, 2, 3].map((k) => {
      const off = base + 4 + k * 5
      return {
        type: block[off],
        codeL: block[off + 1],
        codeH: block[off + 2],
        state: parseDksContactState(block[off + 3] | (block[off + 4] << 8)),
      }
    }) as unknown as DksSlotInfo['keys']
    items.push({ index: i, travels, keys })
  }
  return items
}

/** 解析 MT 信息区为 slot 列表（单击功能 + 长按/绑定功能）。 */
export function parseMtInfo(block: Uint8Array, count = MT_SLOT_COUNT): MtSlotInfo[] {
  const items: MtSlotInfo[] = []
  for (let i = 0; i < count; i++) {
    const base = i * MT_SLOT_SIZE
    items.push({
      index: i,
      tap: { type: block[base], codeL: block[base + 1], codeH: block[base + 2] },
      hold: { type: block[base + 3], codeL: block[base + 4], codeH: block[base + 5] },
    })
  }
  return items
}

/** 解析 TGL 信息区为 slot 列表。 */
export function parseTglInfo(block: Uint8Array, count = TGL_SLOT_COUNT): TglSlotInfo[] {
  const items: TglSlotInfo[] = []
  for (let i = 0; i < count; i++) {
    const base = i * TGL_SLOT_SIZE
    items.push({ index: i, type: block[base], codeL: block[base + 1], codeH: block[base + 2] })
  }
  return items
}

/**
 * 协议字节 → GcomeTriggerType（非法值回落 0=普通）。
 * 低 4bit：0 普通 / 1 RT 关连续 / 2 RT 开连续。
 */
export function parseGcomeTriggerType(raw: number): GcomeTriggerType {
  return raw === 1 || raw === 2 ? raw : 0
}

/**
 * 协议字节 → GcomeSocdStrategy（非法值回落 0x00=后覆盖）。
 * 高 4bit：0x00 / 0x10 / 0x20 / 0x30。
 */
export function parseGcomeSocdStrategy(raw: number): GcomeSocdStrategy {
  return raw === 0x10 || raw === 0x20 || raw === 0x30 ? raw : 0x00
}

/**
 * 解析触发行程参数区为 GCOME 行程列表（family: KeyTravelFamily.Gcome）。
 * 每键 8 字节（见文件头说明）；字节2-3 触发行程为小端 16 位（0-399）。
 */
export function parseTravelInfo(block: Uint8Array, count = TRAVEL_KEY_COUNT): GcomeKeyTravelInfo[] {
  const items: GcomeKeyTravelInfo[] = []
  for (let i = 0; i < count; i++) {
    const base = i * TRAVEL_KEY_SIZE
    items.push({
      family: KeyTravelFamily.Gcome,
      index: i,
      keyTravel: {
        axisType: block[base] & SHAFT_TYPE_ID_MASK,
        triggerType: parseGcomeTriggerType(block[base + 1] & 0x0f),
        socdStrategy: parseGcomeSocdStrategy(block[base + 1] & 0xf0),
        travel: block[base + 2] | ((block[base + 3] & 0x01) << 8),
        precisionBits: (block[base + 3] >> 1) & 0x0f,
        pressRt: block[base + 4] | ((block[base + 5] & 0x01) << 8),
        pressDeadzone: block[base + 5] >> 1,
        releaseRt: block[base + 6] | ((block[base + 7] & 0x01) << 8),
        releaseDeadzone: block[base + 7] >> 1,
      },
    })
  }
  return items
}

// ==================== 编码 ====================

const clampByte = (v: number) => Math.max(0, Math.min(255, Math.round(v) || 0))

/** 将 DKS slot 列表编码回信息区字节（长度按 count 补齐）。 */
export function encodeDksInfo(items: DksSlotInfo[], count = DKS_SLOT_COUNT): Uint8Array {
  const block = new Uint8Array(count * DKS_SLOT_SIZE)
  for (const item of items) {
    if (item.index < 0 || item.index >= count) continue
    const base = item.index * DKS_SLOT_SIZE
    block[base] = clampByte(item.travels[0])
    block[base + 1] = clampByte(item.travels[1])
    block[base + 2] = clampByte(item.travels[2])
    block[base + 3] = clampByte(item.travels[3])
    for (let k = 0; k < 4; k++) {
      const off = base + 4 + k * 5
      const key = item.keys[k]
      block[off] = clampByte(key.type)
      block[off + 1] = clampByte(key.codeL)
      block[off + 2] = clampByte(key.codeH)
      const state = encodeDksContactState(key.state)
      block[off + 3] = state & 0xff
      block[off + 4] = state >> 8
    }
  }
  return block
}

/** 将 MT slot 列表编码回信息区字节（长度按 count 补齐）。 */
export function encodeMtInfo(items: MtSlotInfo[], count = MT_SLOT_COUNT): Uint8Array {
  const block = new Uint8Array(count * MT_SLOT_SIZE)
  for (const item of items) {
    if (item.index < 0 || item.index >= count) continue
    const base = item.index * MT_SLOT_SIZE
    block[base] = clampByte(item.tap.type)
    block[base + 1] = clampByte(item.tap.codeL)
    block[base + 2] = clampByte(item.tap.codeH)
    block[base + 3] = clampByte(item.hold.type)
    block[base + 4] = clampByte(item.hold.codeL)
    block[base + 5] = clampByte(item.hold.codeH)
  }
  return block
}

/** 将 TGL slot 列表编码回信息区字节（长度按 count 补齐）。 */
export function encodeTglInfo(items: TglSlotInfo[], count = TGL_SLOT_COUNT): Uint8Array {
  const block = new Uint8Array(count * TGL_SLOT_SIZE)
  for (const item of items) {
    if (item.index < 0 || item.index >= count) continue
    const base = item.index * TGL_SLOT_SIZE
    block[base] = clampByte(item.type)
    block[base + 1] = clampByte(item.codeL)
    block[base + 2] = clampByte(item.codeH)
  }
  return block
}

/** 将触发行程参数列表编码回信息区字节（长度按 count 补齐，字节0 高3bit 校验固定 0x5）。 */
export function encodeTravelInfo(
  items: GcomeKeyTravelInfo[],
  count = TRAVEL_KEY_COUNT,
): Uint8Array {
  const clampTravel = (v: number) => {
    const n = Math.round(Number(v))
    if (Number.isNaN(n)) return 0
    return Math.max(0, Math.min(399, n))
  }
  // RT 为 9 位字段（物理范围 0-511）：0.01mm 精度上限 399（4.00mm），
  // 0.005/0.001 精度上限 500（2.5mm/0.5mm，协议注）；精度上限由上层 UI 约束。
  const clampRt = (v: number) => {
    const n = Math.round(Number(v))
    if (Number.isNaN(n)) return 0
    return Math.max(0, Math.min(511, n))
  }
  const clampDeadzone = (v: number) => {
    const n = Math.round(Number(v))
    if (Number.isNaN(n)) return 0
    return Math.max(0, Math.min(127, n))
  }
  const block = new Uint8Array(count * TRAVEL_KEY_SIZE)
  for (const item of items) {
    if (item.index < 0 || item.index >= count) continue
    const base = item.index * TRAVEL_KEY_SIZE
    const kt = item.keyTravel
    block[base] = SHAFT_TYPE_HEAD | clampByte(kt.axisType & SHAFT_TYPE_ID_MASK)
    block[base + 1] = clampByte(kt.triggerType & 0x0f) | clampByte(kt.socdStrategy & 0xf0)
    const travel = clampTravel(kt.travel)
    block[base + 2] = travel & 0xff
    block[base + 3] = (travel >> 8) & 0x01
    block[base + 3] |= clampByte(kt.precisionBits & 0x0f) << 1
    const pressRt = clampRt(kt.pressRt)
    block[base + 4] = pressRt & 0xff
    block[base + 5] = ((pressRt >> 8) & 0x01) | (clampDeadzone(kt.pressDeadzone) << 1)
    const releaseRt = clampRt(kt.releaseRt)
    block[base + 6] = releaseRt & 0xff
    block[base + 7] = ((releaseRt >> 8) & 0x01) | (clampDeadzone(kt.releaseDeadzone) << 1)
  }
  return block
}
